import React, { useEffect, useState } from 'react'
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Icon } from '@iconify/react';
import { Button } from '@mui/material'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { CCol, CForm, CFormInput, CFormLabel, CFormTextarea, CInputGroup, CInputGroupText, CRow } from '@coreui/react';
import { useAuth } from '../contexts/AuthContext';
import { useCategory } from '../contexts/CategoryContext';
import { Checkbox } from 'primereact/checkbox';
import CInput from './CInput';
import CourseSelection from './CourseSelection';
import ModifierSelection from './ModifierSelection';

const AddCategory = ({ id, categoryDetails, editMode, editCategoryId, setEditCategoryId, setEditMode, categoryVisible, setCategoryVisible, setCategoryDetails, fetchCategories, canCreate, canUpdate, canDelete }) => {
    const { createCategory, updateCategory, getSingleCategory, deleteCategory } = useCategory()
    const { restaurantId } = useAuth();

    const [validated, setValidated] = useState(false);

    const handleClose = () => {
        setCategoryVisible(false);
        setEditMode(false);
        setEditCategoryId(null);
        setCategoryDetails({
            name: '',
            position: '',
            tax_rate: '18.00',
            course_id: null,
            printer_template: '',
            notification_template: "",
            modifiers: [],
            visible: "",
            image: "",
            description: "",
            restaurant_id: restaurantId
        });
    };

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                const data = await deleteCategory(id);
                if (data.error === false) {
                    handleClose();
                    fetchCategories()
                }
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    };

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this category?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCategoryDetails({ ...categoryDetails, image: reader.result })
            };
            reader.readAsDataURL(file);
        }
    };

    const fetchSingleCategory = async () => {
        try {
            const data = await getSingleCategory(id);
            setCategoryDetails(data);
        } catch (error) {
            console.error('Error fetching single category', error);
        }
    };

    useEffect(() => {
        if (editMode && editCategoryId) fetchSingleCategory();
    }, [editMode, editCategoryId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((editMode && !canUpdate) || (!editMode && !canCreate)) return;

        const form = e.currentTarget;
        setValidated(true);

        if (form.checkValidity() === false) {
            e.stopPropagation();

            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            try {
                if (categoryVisible && restaurantId) {

                    const selectedModifiers = categoryDetails.modifiers.length > 0 ? categoryDetails.modifiers.map(modifier => modifier.value) : []
                    const selectedCourse = categoryDetails.course_id ? categoryDetails.course_id.value : null

                    const data = await createCategory({ ...categoryDetails, course_id: selectedCourse, restaurant_id: restaurantId, modifiers: selectedModifiers })
                    if (!data.error) {
                        fetchCategories(data.category)
                        handleClose()
                    }
                } else if (editMode && editCategoryId && restaurantId) {

                    const selectedModifiers = categoryDetails.modifiers.length > 0 ? categoryDetails.modifiers.map(modifier => modifier.value) : []
                    const selectedCourse = categoryDetails.course_id ? categoryDetails.course_id.value : null

                    const data = await updateCategory(id, { ...categoryDetails, course_id: selectedCourse, restaurant_id: restaurantId, modifiers: selectedModifiers })
                    if (!data.error) {
                        handleClose()
                        fetchCategories()
                    }
                }
            } catch (error) {
                console.log(error)
            }
        }
    }

    return (
        <div>
            <ConfirmDialog />
            <Dialog draggable={false} header={`${categoryVisible ? 'Add Category' : 'Edit Category'}`} visible={categoryVisible || (editMode && editCategoryId !== null)} style={{ width: `35vw` }} onHide={handleClose}>
                <CForm onSubmit={handleSubmit} noValidate validated={validated}>
                    <div className='fields-container'>
                        <CRow className="mb-3 mt-3">
                            <CInput
                                label="Name"
                                type="text"
                                className='d-flex'
                                labelClassName='w-50'
                                value={categoryDetails.name}
                                onChange={(value) => setCategoryDetails({ ...categoryDetails, name: value })}
                                required={true}
                                errorMessage='Name is required.'
                            />
                        </CRow>
                        <CRow className="mb-3 mt-3">
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Tax Rate</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='ps-0'>
                                <CInputGroup>
                                    <CInputGroupText>%</CInputGroupText>
                                    <CFormInput
                                        type='number'
                                        step='any'
                                        value={categoryDetails.tax_rate}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            if (newValue >= 0) {
                                                setCategoryDetails({ ...categoryDetails, tax_rate: newValue })
                                            }
                                        }}
                                        feedbackInvalid='Please enter valid tax rate.'
                                    />
                                </CInputGroup>
                            </CCol>
                        </CRow>
                        <CRow className="mb-3">
                            <CourseSelection
                                className='d-flex'
                                labelClassName='w-50'
                                value={categoryDetails.course_id}
                                onChange={(value) => setCategoryDetails({ ...categoryDetails, course_id: value })}
                                multiSelect={false}
                            />
                        </CRow>
                        <CRow className='mb-4'>
                            <ModifierSelection
                                className='d-flex'
                                labelClassName='w-50'
                                allModifiers={true}
                                value={categoryDetails.modifiers}
                                onChange={(value) => setCategoryDetails({ ...categoryDetails, modifiers: value })}
                            />
                        </CRow>
                        <CRow className="mb-3 mt-3">
                            <div className="d-flex">
                                <CFormLabel className='w-50 custom-word-break'>Description</CFormLabel>
                                <CFormTextarea
                                    rows={3}
                                    value={categoryDetails.description}
                                    onChange={(e) => setCategoryDetails({ ...categoryDetails, description: e.target.value })}
                                />
                            </div>
                        </CRow>
                        <CRow className='mb-4'>
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Visible</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='px-1'>
                                <Checkbox
                                    checked={categoryDetails.visible === 1}
                                    onChange={(event) => setCategoryDetails({ ...categoryDetails, visible: event.target.checked ? 1 : 0 })}
                                />
                            </CCol>
                        </CRow>
                        <CRow className="mb-3">
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Image</CFormLabel>
                            </CCol>
                            <CCol lg={8} style={{ position: 'relative' }} className='px-1'>
                                <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="fileInput" />
                                <label htmlFor="fileInput">
                                    <Card
                                        className="create-card"
                                        header={categoryDetails.image ? <LazyLoadImage src={categoryDetails.image || 'defaultImageURL'} alt="User" className='create-img' /> :
                                            <Icon icon="ep:food" style={{ color: 'black' }} height={100} width={100} />}
                                    />
                                </label>
                                {
                                    categoryDetails.image &&
                                    <Icon
                                        icon="mdi:close"
                                        className='close-btn'
                                        height={15}
                                        width={15}
                                        onClick={() => setCategoryDetails({ ...categoryDetails, image: '' })}
                                    />
                                }
                            </CCol>
                        </CRow>
                    </div>
                    <div className={`d-flex button-container ${(editMode && editCategoryId && canDelete) ? 'justify-content-between' : 'justify-content-end'}`}>
                        {
                            (editMode && editCategoryId && canDelete) && (
                                <div>
                                    <Button color='error' variant="contained" onClick={handleDelete} >
                                        Delete
                                    </Button>
                                </div>
                            )
                        }
                        <div className='d-flex'>
                            <Button onClick={handleClose} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            {((!editMode && canCreate) || (editMode && canUpdate)) && (
                                <Button fullWidth variant="contained" color="primary" type="submit" className="ms-3">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </div>
                </CForm>
            </Dialog>
        </div>
    )
}

export default AddCategory
